package com.essntl.features.itinerary.ui.edit_itinerary_service.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextAlign
import com.essntl.core.utils.utils.capitalizeFirstLetter
import com.essntl.features.address.domain.model.AddressModel
import com.essntl.features.address.ui.component.AddressDropDown
import com.essntl.features.contact.domain.model.ContactModel
import com.essntl.features.contact.ui.ContactEvent
import com.essntl.features.contact.ui.ContactState
import com.essntl.features.contact.ui.components.ContactDropdownType
import com.essntl.features.contact.ui.components.ContactsDropDown
import com.essntl.features.currency.domain.repository.CurrencyRepository
import com.essntl.features.currency.ui.EsCurrencyPriceInput
import com.essntl.features.itinerary.domain.model.ItineraryServiceModel
import com.essntl.features.itinerary.ui.edit_itinerary_service.EditItineraryServiceEvent
import com.essntl.features.itinerary.ui.edit_itinerary_service.EditItineraryServiceNavigation
import com.essntl.features.itinerary.ui.edit_itinerary_service.EditItineraryServiceState
import com.essntl.features.service.ui.components.richtext.ServiceRichTextEditor
import com.essntl.uikit.components.checkbox.EsPrimaryCheckBox
import com.essntl.uikit.components.expandable.ExpandableTabs
import com.essntl.uikit.components.iconbutton.outline.EsSmallOutlineIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsLargeIcon
import com.essntl.uikit.components.icons.EsSmallIcon
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import com.mohamedrejeb.richeditor.model.rememberRichTextState
import kotlinx.coroutines.launch
import org.koin.compose.koinInject

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ColumnScope.EditItineraryServiceDescription(
    itineraryService: ItineraryServiceModel,
    state: EditItineraryServiceState,
    onEvent: (EditItineraryServiceEvent) -> Unit,
    onNavigation: (EditItineraryServiceNavigation) -> Unit,
    contactState: ContactState,
    contactEvent: (ContactEvent) -> Unit,
) {
    val scope = rememberCoroutineScope()
    val currencyRepository = koinInject<CurrencyRepository>()

    val tabList = remember {
        listOf(
            "Details",
            "Address",
            "Contact",
        )
    }

    ExpandableTabs(
        tabList = tabList,
        id = itineraryService.id,
        modifier = Modifier
            .background(
                color = MaterialTheme.esColorScheme.bgSecondary,
                shape = RoundedCornerShape(EsConstants.RoundedCornerSize),
            )
            .fillMaxWidth()
            .weight(1f),
    ) { index ->
        when (index) {
            0 -> {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = 1.5.spacing),
                ) {
                    EsInput(
                        value = itineraryService.additionalInfo ?: "",
                        onValueChange = {
                            onEvent(
                                EditItineraryServiceEvent.UpdateItineraryServiceFields(
                                    itineraryService.copy(
                                        additionalInfo = it,
                                    ),
                                ),
                            )
                        },
                        placeholder = "Enter additional info",
                        singleLine = false,
                        label = "Additional info",
                    )

                    Spacer(modifier = Modifier.height(height = 3.spacing))

                    val richTextState = rememberRichTextState()
                    LaunchedEffect(itineraryService.id) {
                        richTextState.setHtml(itineraryService.editedDescription ?: "")
                    }

                    LaunchedEffect(richTextState.annotatedString) {
                        onEvent(
                            EditItineraryServiceEvent.UpdateItineraryServiceFields(
                                itineraryService.copy(
                                    editedDescription = richTextState.toHtml(),
                                ),
                            ),
                        )
                    }
                    ServiceRichTextEditor(
                        richTextState = richTextState,
                        modifier = Modifier
                            .fillMaxWidth(),
                        onRewriteWithAI = {},
                    )

                    Spacer(modifier = Modifier.height(height = 1.5.spacing))

                    Row(
                        horizontalArrangement = Arrangement.spacedBy(1.spacing),
                        modifier = Modifier.fillMaxWidth(),
                    ) {
                        Column(
                            modifier = Modifier.weight(1f),
                        ) {
                            EsCurrencyPriceInput(
                                value = (itineraryService.price ?: 0) / 100.0,
                                code = itineraryService.priceCurrency?.code,
                                label = "Price",
                                onValueChange = {
                                    val price = (it * 100).toInt()

                                    onEvent(
                                        EditItineraryServiceEvent.UpdateItineraryServiceFields(
                                            itineraryService.copy(
                                                price = price,
                                            ),
                                        ),
                                    )
                                },
                                onCodeChange = {
                                    scope.launch {
                                        currencyRepository.getByCode(it)
                                            .onSuccess { c ->
                                                onEvent(
                                                    EditItineraryServiceEvent.UpdateItineraryServiceFields(
                                                        itineraryService.copy(
                                                            priceCurrency = c,
                                                        ),
                                                    ),
                                                )
                                            }
                                    }
                                },
                                modifier = Modifier.fillMaxWidth(),
                            )
                        }

                        Column(
                            modifier = Modifier.weight(1f),
                        ) {
                            EsCurrencyPriceInput(
                                value = (itineraryService.cost ?: 0) / 100.0,
                                code = itineraryService.costCurrency?.code,
                                label = "Cost",
                                onValueChange = {
                                    val cost = (it * 100).toInt()

                                    onEvent(
                                        EditItineraryServiceEvent.UpdateItineraryServiceFields(
                                            itineraryService.copy(
                                                cost = cost,
                                            ),
                                        ),
                                    )
                                },
                                onCodeChange = {
                                    scope.launch {
                                        currencyRepository.getByCode(it)
                                            .onSuccess { c ->
                                                onEvent(
                                                    EditItineraryServiceEvent.UpdateItineraryServiceFields(
                                                        itineraryService.copy(
                                                            costCurrency = c,
                                                        ),
                                                    ),
                                                )
                                            }
                                    }
                                },
                                modifier = Modifier
                                    .fillMaxWidth(),
                            )
                        }
                    }

                    Spacer(modifier = Modifier.height(height = 1.5.spacing))

                    Row(
                        horizontalArrangement = Arrangement.Center,
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        EsPrimaryCheckBox(
                            checked = itineraryService.bookedByTa ?: false,
                            onCheckedChange = {
                                onEvent(
                                    EditItineraryServiceEvent.UpdateItineraryServiceFields(
                                        itineraryService.copy(
                                            bookedByTa = it,
                                        ),
                                    ),
                                )
                            },
                            modifier = Modifier
                                .focusProperties { canFocus = false }
                                .align(Alignment.CenterVertically),
                        )

                        Spacer(modifier = Modifier.width(2.spacing))

                        Text(
                            text = "Booked by Travel Agent",
                            style = MaterialTheme.esTypography.p1Regular16,
                            color = MaterialTheme.esColorScheme.textPrimary,
                        )
                    }

                    Spacer(modifier = Modifier.height(height = 1.5.spacing))

                    EsInput(
                        value = itineraryService.embeddedContent ?: "",
                        onValueChange = {
                            onEvent(
                                EditItineraryServiceEvent.UpdateItineraryServiceFields(
                                    itineraryService.copy(
                                        embeddedContent = it,
                                    ),
                                ),
                            )
                        },
                        placeholder = "Enter embedded content link",
                        singleLine = false,
                        label = "Embed a content",
                    )

                    Spacer(modifier = Modifier.height(height = 3.spacing))
                }
            }

            1 -> {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = 1.5.spacing),
                ) {
                    Text(
                        text = "Full Address",
                        style = MaterialTheme.esTypography.p2Medium14,
                    )

                    Spacer(modifier = Modifier.height(height = 1.5.spacing))

                    AddressDropDown(
                        address = itineraryService.address,
                        selectedPlace = state.selectedPlace,
                        allPlaces = state.getAllPlaces.data.orEmpty(),
                        onGetPlaces = { onEvent(EditItineraryServiceEvent.GetPlaces(it)) },
                        onSelectPlace = { onEvent(EditItineraryServiceEvent.SelectPlace(it)) },
                        onClear = {
                            onEvent(
                                EditItineraryServiceEvent.UpdateItineraryServiceFields(
                                    itineraryService = state.itineraryService.value.copy(
                                        address = AddressModel.empty,
                                    ),
                                ),
                            )
                        },
                    )

                    Spacer(modifier = Modifier.height(height = 1.5.spacing))

                    Text(
                        text = "Pick-up or Meeting point",
                        style = MaterialTheme.esTypography.p2Medium14,
                    )

                    Spacer(modifier = Modifier.height(height = 1.5.spacing))

                    EsInput(
                        value = itineraryService.meetingPoint.orEmpty(),
                        onValueChange = {
                            onEvent(
                                EditItineraryServiceEvent.UpdateItineraryServiceFields(
                                    itineraryService.copy(
                                        meetingPoint = it,
                                    ),
                                ),
                            )
                        },
                        placeholder = "Enter Meeting Point",
                        modifier = Modifier.fillMaxWidth(),
                    )

                    Spacer(modifier = Modifier.height(height = 3.spacing))
                }
            }

            2 -> {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = 1.5.spacing),
                ) {
                    val enabled = itineraryService.supplier?.contact?.organization?.id != null

                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(1.spacing),
                        modifier = Modifier.fillMaxWidth(),
                    ) {
                        Box(
                            contentAlignment = Alignment.Center,
                            modifier = Modifier.weight(8f),
                        ) {
                            ContactsDropDown(
                                state = contactState,
                                onEvent = contactEvent,
                                type = ContactDropdownType.SUPPLIER_CONTACT,
                                selectedContact = itineraryService.supplierContact?.id,
                                organizationId = itineraryService.supplier?.contact?.organization?.id,
                                defaultText = itineraryService.supplierContact?.prettyNamePhone,
                                label = itineraryService.supplier?.contact?.organization?.name?.let {
                                    "Contact from ${it.capitalizeFirstLetter()}"
                                } ?: "Contact from supplier",
                                enabled = enabled,
                                placeholder = "Select existing",
                                onItemSelected = { id ->
                                    onEvent(
                                        EditItineraryServiceEvent.UpdateItineraryServiceFields(
                                            itineraryService.copy(
                                                supplierContact = ContactModel.empty.copy(id = id),
                                            ),
                                        ),
                                    )
                                },
                            )
                        }

                        if (itineraryService.supplierContact != null)
                            EsSmallOutlineIconButton(
                                onClick = {
                                    onEvent(
                                        EditItineraryServiceEvent.UpdateItineraryServiceFields(
                                            itineraryService.copy(
                                                supplierContact = null,
                                            ),
                                        ),
                                    )
                                },
                                iconRes = EsIcons.Close,
                                enabled = true,
                            )
                    }

                    Row(
                        horizontalArrangement = Arrangement.spacedBy(0.5.spacing),
                    ) {
                        if (enabled)
                            itineraryService.supplierContact?.let { c ->
                                Row(
                                    horizontalArrangement = Arrangement.spacedBy(0.5.spacing),
                                    verticalAlignment = Alignment.CenterVertically,
                                    modifier = Modifier
                                        .clip(RoundedCornerShape(2.spacing))
                                        .clickable {
                                            onNavigation(EditItineraryServiceNavigation.EditContact(c))
                                        }
                                        .padding(1.spacing),
                                ) {
                                    Text(
                                        text = "Edit Contact",
                                        textAlign = TextAlign.Start,
                                        style = MaterialTheme.esTypography.p1Medium16,
                                        color = Color.Black,
                                    )

                                    EsSmallIcon(
                                        res = EsIcons.Edit,
                                        tint = Color.Black,
                                    )
                                }
                            }

                        Row(
                            modifier = Modifier
                                .clip(RoundedCornerShape(2.spacing))
                                .clickable(
                                    enabled = enabled,
                                ) {
                                    onNavigation(EditItineraryServiceNavigation.AddContact)
                                }
                                .padding(1.spacing),
                        ) {
                            Text(
                                text = if (enabled)
                                    "Create New Contact" else
                                    "Please select a supplier first",
                                textAlign = TextAlign.Start,
                                style = MaterialTheme.esTypography.p1Medium16,
                                color = if (enabled)
                                    MaterialTheme.esColorScheme.textAccent else
                                    Color.Black,
                            )

                            if (enabled)
                                EsLargeIcon(
                                    res = EsIcons.Add,
                                    tint = MaterialTheme.esColorScheme.iconAccent,
                                )
                        }
                    }

                    Spacer(modifier = Modifier.height(height = 3.spacing))
                }
            }
        }
    }
}